import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { creatorbase, creatorbaseQueries, outreach, outreachQueries } from '@round/api';
import { MoneyValue } from '@round/creatorbase-ui';
import {
    useAddOutreachResultMutation,
    useCreatePost,
    useInvalidateAggregatedPostStats,
    useInvalidateCampaigns,
    useInvalidatePosts,
    useInvalidateProject,
} from '@round/creatorbase-helpers/api-hooks';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { ProjectCampaignOption } from 'Modules/Plans/CreatorSearch/components/AddCreatorToCampaignsDropdown/useProjectCampaignGroups';
import { usePatchOutreachResultsCache } from '../../hooks/usePatchOutreachResultsCache';
import { OutreachResultRow } from '../../helpers';

const addUtils = outreachQueries.outreachRequestResults.utils.add;
const postUtils = creatorbaseQueries.posts.utils.create;

export const NO_ACCOUNT_ERROR = "Couldn't reach this creator's account. Please try again";

export type ResultCost = {
    result: OutreachResultRow;
    cost: MoneyValue | null;
};

export type AddResultsToCampaignOutcome = {
    addedResultIds: number[];
    failedCount: number;
    /** What the endpoint said about the first refusal; null when there was none. */
    errorMessage: string | null;
};

const toAmount = (cost: MoneyValue | null) => (cost?.value ? Number(cost.value) : undefined);

/** Adds a batch of results to one campaign, one post per result, so a refusal on one leaves the rest to complete. */
export function useAddResultsToCampaign() {
    const queryClient = useQueryClient();
    const patchResultsCache = usePatchOutreachResultsCache();
    const { selectedClient } = useClientSelect();
    const areFeesEnabled = !!selectedClient?.is_payment_enabled;

    // Unkeyed, unlike a row's own mutations: a batch belongs to no one row.
    const { addAsync } = useAddOutreachResultMutation(null, queryClient);
    const { createAsync } = useCreatePost();

    const invalidatePosts = useInvalidatePosts();
    const invalidateAggregatedPostStats = useInvalidateAggregatedPostStats();
    const invalidateCampaigns = useInvalidateCampaigns();
    const invalidateProject = useInvalidateProject();

    const [isAdding, setIsAdding] = useState(false);

    const addResultsToCampaign = async (
        campaign: ProjectCampaignOption,
        entries: ResultCost[]
    ): Promise<AddResultsToCampaignOutcome> => {
        if (!entries.length || isAdding) {
            return { addedResultIds: [], failedCount: 0, errorMessage: null };
        }

        setIsAdding(true);

        // Held back until the batch settles: writing rows in mid-flight rearranges the table.
        const addedToBase: outreach.OutreachResult[] = [];

        try {
            const settled = await Promise.allSettled(
                entries.map(async ({ result, cost }) => {
                    let accountId = result.client_influencer_account_id;

                    if (!accountId) {
                        const added = await addAsync({ id: result.id });
                        addedToBase.push(added);
                        accountId = added.client_influencer_account_id;
                    }

                    if (!accountId) {
                        throw new Error(NO_ACCOUNT_ERROR);
                    }

                    const payload: creatorbase.PostPostData = {
                        campaign_id: campaign.id,
                        client_influencer_account_id: accountId,
                    };

                    // With fees, the endpoint works them out from a base cost in the creator's own currency.
                    if (areFeesEnabled) {
                        payload.base_cost = toAmount(cost);
                        payload.currency_id = cost?.currency?.value;
                    } else {
                        payload.cost = toAmount(cost);
                    }

                    await createAsync(payload);

                    return result.id;
                })
            );

            // The add stands whether or not the post did, so every row it moved catches up with it.
            patchResultsCache(addedToBase);

            const failed = settled.filter((outcome) => outcome.status === 'rejected');

            if (settled.length > failed.length) {
                // One campaign for the whole batch, so its lists and totals are dated once.
                invalidatePosts({ campaign_id: campaign.id.toString() });
                invalidateAggregatedPostStats({ campaign_id: campaign.id });
                invalidateAggregatedPostStats({ project_id: campaign.project_id, platform: campaign.platform });
                invalidateCampaigns(campaign.id);
                invalidateProject(campaign.project_id);
            }

            return {
                addedResultIds: settled
                    .filter((outcome) => outcome.status === 'fulfilled')
                    .map((outcome) => outcome.value),
                failedCount: failed.length,
                errorMessage: failed.length ? getErrorMessage(failed[0].reason) : null,
            };
        } finally {
            setIsAdding(false);
        }
    };

    return { addResultsToCampaign, isAdding };
}

/** A refusal comes from either call, so it is read back by whichever of them can recognise it. */
function getErrorMessage(reason: unknown) {
    const error = reason as Error;

    if (addUtils.isExpectedErrorResponse(error)) {
        return addUtils.getErrorMessage(error);
    }

    if (postUtils.isExpectedErrorResponse(error)) {
        return postUtils.getErrorMessage(error);
    }

    return error.message;
}
